import React, { useState, useEffect } from 'react';
import ReactPlayer from 'react-player';
import { PlayCircle, Volume2 } from 'lucide-react';

const TestVideoDebug = () => {
  const [playerReady, setPlayerReady] = useState(false);
  const [playerError, setPlayerError] = useState(null);
  const [consoleMessages, setConsoleMessages] = useState([]);

  // The specific video URL from the review request
  const videoUrl = "https://www.youtube.com/watch?v=dQw4w9WgXcQ";
  
  // Course and lesson details from review request
  const courseId = "850783f3-8265-41ba-bab5-09fc966143b5";
  const lessonId = "686f7649-9d57-41c8-a4ce-3b4543cf29ec";

  useEffect(() => {
    // Capture console messages
    const originalConsoleLog = console.log;
    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;

    console.log = (...args) => {
      setConsoleMessages(prev => [...prev, { type: 'log', message: args.join(' '), timestamp: new Date().toISOString() }]);
      originalConsoleLog(...args);
    };

    console.error = (...args) => {
      setConsoleMessages(prev => [...prev, { type: 'error', message: args.join(' '), timestamp: new Date().toISOString() }]);
      originalConsoleError(...args);
    };

    console.warn = (...args) => {
      setConsoleMessages(prev => [...prev, { type: 'warn', message: args.join(' '), timestamp: new Date().toISOString() }]);
      originalConsoleWarn(...args);
    };

    return () => {
      console.log = originalConsoleLog;
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
    };
  }, []);

  const handlePlayerReady = () => {
    console.log('ReactPlayer is ready');
    setPlayerReady(true);
  };

  const handlePlayerError = (error) => {
    console.error('ReactPlayer error:', error);
    setPlayerError(error);
  };

  const handlePlayerStart = () => {
    console.log('ReactPlayer started playing');
  };

  const handlePlayerProgress = (progress) => {
    console.log('ReactPlayer progress:', progress);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8 text-center">YouTube Video Debug Test</h1>
        
        {/* Test Information */}
        <div className="bg-blue-50 p-6 rounded-lg mb-8">
          <h2 className="text-xl font-semibold mb-4">Test Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <strong>Course ID:</strong> {courseId}
            </div>
            <div>
              <strong>Lesson ID:</strong> {lessonId}
            </div>
            <div>
              <strong>Video URL:</strong> {videoUrl}
            </div>
            <div>
              <strong>Expected Video ID:</strong> dQw4w9WgXcQ
            </div>
          </div>
        </div>

        {/* Video Player Test */}
        <div className="bg-white p-6 rounded-lg shadow-lg mb-8">
          <div className="flex items-center gap-2 mb-4">
            <PlayCircle className="w-5 h-5" style={{ color: '#102862' }} />
            <h2 className="text-xl font-semibold">TEST VIDEO - ReactPlayer Implementation</h2>
          </div>
          
          <div className="relative aspect-video bg-black rounded-lg overflow-hidden mb-4">
            <ReactPlayer
              url={videoUrl}
              width="100%"
              height="100%"
              controls
              muted={true}
              onReady={handlePlayerReady}
              onError={handlePlayerError}
              onStart={handlePlayerStart}
              onProgress={handlePlayerProgress}
              config={{
                youtube: {
                  playerVars: {
                    showinfo: 1,
                    controls: 1,
                    modestbranding: 1,
                    rel: 0
                  }
                }
              }}
            />
          </div>

          {/* Player Status */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className={`p-3 rounded ${playerReady ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
              <strong>Player Ready:</strong> {playerReady ? 'Yes' : 'No'}
            </div>
            <div className={`p-3 rounded ${playerError ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
              <strong>Player Error:</strong> {playerError ? 'Yes' : 'No'}
            </div>
            <div className="p-3 rounded bg-blue-100 text-blue-800">
              <strong>ReactPlayer Version:</strong> {ReactPlayer.version || 'Unknown'}
            </div>
          </div>

          {playerError && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded">
              <h3 className="font-semibold text-red-800 mb-2">Player Error Details:</h3>
              <pre className="text-sm text-red-700 whitespace-pre-wrap">{JSON.stringify(playerError, null, 2)}</pre>
            </div>
          )}
        </div>

        {/* ReactPlayer Capabilities Check */}
        <div className="bg-white p-6 rounded-lg shadow-lg mb-8">
          <h2 className="text-xl font-semibold mb-4">ReactPlayer Capabilities</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <strong>Can Play YouTube:</strong> {ReactPlayer.canPlay(videoUrl) ? 'Yes' : 'No'}
            </div>
            <div>
              <strong>Can Play YouTube (generic):</strong> {ReactPlayer.canPlay('https://www.youtube.com/watch?v=test') ? 'Yes' : 'No'}
            </div>
          </div>
        </div>

        {/* Console Messages */}
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-semibold mb-4">Console Messages</h2>
          <div className="max-h-96 overflow-y-auto">
            {consoleMessages.length === 0 ? (
              <p className="text-gray-500">No console messages yet...</p>
            ) : (
              <div className="space-y-2">
                {consoleMessages.map((msg, index) => (
                  <div key={index} className={`p-2 rounded text-sm ${
                    msg.type === 'error' ? 'bg-red-50 text-red-800' :
                    msg.type === 'warn' ? 'bg-yellow-50 text-yellow-800' :
                    'bg-gray-50 text-gray-800'
                  }`}>
                    <div className="flex justify-between items-start">
                      <span className="font-mono">[{msg.type.toUpperCase()}] {msg.message}</span>
                      <span className="text-xs opacity-60">{new Date(msg.timestamp).toLocaleTimeString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Network Requests Info */}
        <div className="bg-white p-6 rounded-lg shadow-lg mt-8">
          <h2 className="text-xl font-semibold mb-4">Debug Instructions</h2>
          <div className="text-sm space-y-2">
            <p><strong>1. Check Browser Console:</strong> Open DevTools (F12) and look for any errors in the Console tab</p>
            <p><strong>2. Check Network Tab:</strong> Look for requests to youtube.com, googlevideo.com, or ytimg.com</p>
            <p><strong>3. Check iframe Creation:</strong> Inspect the video area to see if an iframe is created</p>
            <p><strong>4. Expected iframe src:</strong> Should contain "https://www.youtube.com/embed/dQw4w9WgXcQ"</p>
            <p><strong>5. CSP Issues:</strong> Look for Content Security Policy errors that might block YouTube</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestVideoDebug;